import React, { useState, useEffect } from 'react';
import { Search, X, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

/**
 * EncuestasEstudiante - Módulo para ver y completar encuestas
 * Encuestas creadas por Jefatura de Carrera, guardadas en tabla 'encuestas'
 * Campos: nombre, objetivo, fecha_publicacion, fecha_cierre, formulario (JSON con preguntas)
 */
const EncuestasEstudiante = () => {
  const [encuestas, setEncuestas] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [encuestaSeleccionada, setEncuestaSeleccionada] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadEncuestas();
  }, []);

  const loadEncuestas = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/estudiante/encuestas`, { headers });
      if (response.ok) {
        const data = await response.json();
        const encuestasData = data.encuestas || [];
        // Ordenar por ID más reciente primero (o por fecha de creación si disponible)
        encuestasData.sort((a, b) => (b.id_encuesta || 0) - (a.id_encuesta || 0));
        setEncuestas(encuestasData);
      } else {
        setEncuestas([]);
      }
    } catch (error) {
      console.error('Error loading encuestas:', error);
      setEncuestas([]);
    } finally {
      setLoading(false);
    }
  };

  // Abrir encuesta para completar
  const handleCompletar = async (encuesta) => {
    if (encuesta.estado_estudiante === 'Completada') return;

    // Verificar si la fecha de cierre ya pasó
    if (encuesta.fecha_cierre) {
      const fechaCierre = new Date(encuesta.fecha_cierre);
      const ahora = new Date();
      if (ahora > fechaCierre) {
        alert('Esta encuesta ya ha cerrado y no puede ser completada.');
        return;
      }
    }

    // El formulario puede venir como string JSON o como objeto
    let formularioData = encuesta.formulario;
    if (typeof formularioData === 'string') {
      try {
        formularioData = JSON.parse(formularioData);
      } catch (e) {
        console.error('Error parsing formulario:', e);
        formularioData = null;
      }
    }

    // Las preguntas pueden estar en formulario.preguntas o en preguntas directamente
    let preguntasData = [];
    if (formularioData?.preguntas) {
      preguntasData = formularioData.preguntas;
    } else if (encuesta.preguntas) {
      // El campo preguntas puede venir como string JSON también
      if (typeof encuesta.preguntas === 'string') {
        try {
          preguntasData = JSON.parse(encuesta.preguntas);
        } catch (e) {
          preguntasData = [];
        }
      } else {
        preguntasData = encuesta.preguntas;
      }
    }

    setEncuestaSeleccionada({
      ...encuesta,
      preguntas: preguntasData
    });
    setRespuestas({});
  };

  // Cambiar respuesta
  const handleRespuestaChange = (preguntaIdx, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaIdx]: valor
    }));
  };

  // Enviar encuesta
  const handleEnviar = async () => {
    if (!encuestaSeleccionada) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(`${API_URL}/estudiante/encuestas/${encuestaSeleccionada.id_encuesta}/responder`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ respuestas })
      });

      if (response.ok) {
        // Actualizar estado local
        setEncuestas(prev => prev.map(e =>
          e.id_encuesta === encuestaSeleccionada.id_encuesta
            ? { ...e, estado_estudiante: 'Completada' }
            : e
        ));
        setEncuestaSeleccionada(null);
        setRespuestas({});
        alert('Encuesta enviada exitosamente!');
      } else {
        const error = await response.json();
        alert(error.message || 'Error al enviar encuesta');
      }
    } catch (error) {
      console.error('Error enviando encuesta:', error);
      alert('Error al enviar encuesta');
    } finally {
      setSubmitting(false);
    }
  };

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    try {
      return new Date(fecha).toLocaleString('es-BO', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return fecha;
    }
  };

  // Filtrar encuestas por búsqueda (en todos los campos)
  const encuestasFiltradas = encuestas.filter(e => {
    const searchLower = search.toLowerCase();
    return (
      e.nombre?.toLowerCase().includes(searchLower) ||
      e.objetivo?.toLowerCase().includes(searchLower) ||
      e.estado_estudiante?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <Loader className="animate-spin mr-2" /> Cargando encuestas...
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">Estudiante &gt; Encuestas</h1>
      </div>

      {/* Buscador genérico */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
      </div>

      {/* Tabla de encuestas */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-3 border text-center">No</th>
              <th className="px-3 py-3 border text-left">Encuesta</th>
              <th className="px-3 py-3 border text-left">Objetivo Encuesta</th>
              <th className="px-3 py-3 border text-center">Hora Publicación</th>
              <th className="px-3 py-3 border text-center">Hora Cierre</th>
              <th className="px-3 py-3 border text-center">Acciones</th>
              <th className="px-3 py-3 border text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {encuestasFiltradas.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No hay encuestas disponibles
                </td>
              </tr>
            ) : (
              encuestasFiltradas.map((enc, idx) => (
                <tr key={enc.id_encuesta} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 border text-center">{idx + 1}</td>
                  <td className="px-4 py-3 border">{enc.nombre}</td>
                  <td className="px-4 py-3 border">{enc.objetivo || '-'}</td>
                  <td className="px-4 py-3 border text-center">{formatFecha(enc.fecha_publicacion)}</td>
                  <td className="px-4 py-3 border text-center">{formatFecha(enc.fecha_cierre)}</td>
                  <td className="px-4 py-3 border text-center">
                    {enc.estado_estudiante === 'Pendiente' ? (
                      (() => {
                        const isCerrada = enc.fecha_cierre && new Date() > new Date(enc.fecha_cierre);
                        return isCerrada ? (
                          <span className="text-gray-400">Cerrada</span>
                        ) : (
                          <button
                            onClick={() => handleCompletar(enc)}
                            className="text-blue-600 underline hover:text-blue-800 font-medium"
                          >
                            Completar
                          </button>
                        );
                      })()
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 border text-center">
                    {(() => {
                      const isCerrada = enc.fecha_cierre && new Date() > new Date(enc.fecha_cierre);
                      const estado = enc.estado_estudiante === 'Completada' ? 'Completada' : (isCerrada ? 'No completada' : 'Pendiente');
                      const colorClass = estado === 'Completada' ? 'bg-green-200 text-green-800' :
                        (estado === 'No completada' ? 'bg-gray-200 text-gray-800' : 'bg-blue-200 text-blue-800');
                      return (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                          {estado}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ========== MODAL ENCUESTA ========== */}
      {encuestaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header del modal */}
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">{encuestaSeleccionada.nombre || 'Encuesta'}</h2>
              <button
                onClick={() => setEncuestaSeleccionada(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Contenido de la encuesta */}
            <div className="p-6">
              {encuestaSeleccionada.preguntas?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Esta encuesta no tiene preguntas configuradas.</p>
              ) : (
                encuestaSeleccionada.preguntas?.map((pregunta, idx) => (
                  <div key={idx} className="mb-6">
                    <p className="font-medium mb-2">{idx + 1}. {pregunta.texto}</p>

                    {pregunta.tipo === 'cerrado' ? (
                      <div className="ml-4 space-y-1">
                        {pregunta.opciones?.map((opcion, opIdx) => (
                          <label key={opIdx} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`pregunta_${idx}`}
                              value={opcion}
                              checked={respuestas[idx] === opcion}
                              onChange={() => handleRespuestaChange(idx, opcion)}
                              className="text-blue-600"
                            />
                            <span>{String.fromCharCode(97 + opIdx)}) {opcion}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <textarea
                        value={respuestas[idx] || ''}
                        onChange={(e) => handleRespuestaChange(idx, e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border rounded-md resize-none"
                        placeholder="Escriba su respuesta..."
                      />
                    )}
                  </div>
                ))
              )}

              {/* Botón enviar */}
              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={handleEnviar}
                  disabled={submitting || encuestaSeleccionada.preguntas?.length === 0}
                  className="px-8 py-2 bg-blue-900 text-white rounded-md font-medium hover:bg-blue-800 transition disabled:bg-gray-400 flex items-center gap-2"
                >
                  {submitting && <Loader className="animate-spin" size={16} />}
                  Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EncuestasEstudiante;